// Pure layout renderer transferred from the reference collage template.
// No prototype state, demo photos, API calls or order actions are imported.
const U = 1000;
const esc = value =>
  String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
const ic = () => '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 5v14M5 12h14"/></svg>';
const rng = seed => {
  let s = (seed * 9301 + 49297) >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
};
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONN = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const GAPS = [
  ['0', 'Без отступов', 0, 0],
  ['s', 'Тонкие', 14, 10],
  ['m', 'Средние', 30, 18],
  ['l', 'Широкие', 54, 28],
];
const LAYS = [
  ['mosaic', 'Мозаика', 'Кладка из ячеек разной высоты', 4, 9, 6, 'band', 'Сетки Свободно', 96, 0, 'Хит'],
  ['g22', 'Сетка 2×2', 'Четыре равные ячейки', 4, 4, 4, 'band', 'Сетки', 90],
  ['pol', 'Полароиды', 'Карточки в белых рамках, с наклоном', 2, 9, 4, 'pol', 'Свободно Ретро', 88],
  ['film', 'Плёнка', 'Кадры на плёнке и поле для подписи', 3, 4, 4, 'film', 'Ретро С текстом', 84, 1],
  ['hero2', 'Герой и два', 'Крупное фото и два рядом', 3, 3, 3, 'band', 'Сетки', 82],
  ['g33', 'Сетка 3×3', 'Подборки и итоги года', 9, 9, 9, 'band', 'Сетки', 80],
  ['hero', 'Герой и лента', 'Крупное фото и лента мелких', 3, 5, 4, 'band', 'Сетки', 78],
  ['mag', 'Журнал', 'Заголовок, крупное фото и сноски', 3, 5, 4, 'mag', 'С текстом', 76],
  ['story', 'Сторис', 'Ленты во всю ширину, лучше в 9:16', 2, 6, 3, 'band', 'Для соцсетей', 74],
  ['circles', 'Круги', 'Фото в круглых окнах', 2, 7, 5, 'band', 'Свободно', 70],
  ['g23', 'Сетка 2×3', 'Шесть равных ячеек', 6, 6, 6, 'band', 'Сетки', 68],
  ['ba', 'До и после', 'Два фото с подписями', 2, 2, 2, 'band', 'Для соцсетей', 66],
  ['mood', 'Мудборд', 'Скотч, наклон и заголовок', 3, 9, 6, 'mood', 'Свободно С текстом', 64, 1],
  ['pano', 'Панорама', 'Один кадр на несколько слайдов карусели', 2, 6, 4, 'band', 'Для соцсетей', 62, 1],
].map(([id, n, d, min, max, ex, tx, th, pop, nw, b]) => ({
  id,
  n,
  d,
  min,
  max,
  ex,
  tx,
  th: th.split(' '),
  pop,
  isNew: !!nw,
  b: b || (nw ? 'Новое' : ''),
}));

const arPair = a => {
  const [w, h] = String(a || '1:1')
    .split(':')
    .map(Number);
  return [w || 1, h || 1];
};
/** Слайдов у «Панорамы»: 2–3 фото — 2, 4–5 — 3, 6 — 4 */
const slidesOf = k => (k <= 3 ? 2 : k <= 5 ? 3 : 4);
/** Пропорции холста раскладки L при k ячейках: формат; у «Панорамы» — формат слайда × число слайдов */
const canvasAr = (L, k, ar) => {
  const [w, h] = arPair(ar);
  return L.id === 'pano' ? [w * slidesOf(k), h] : [w, h];
};
/** Сколько ячеек сейчас у раскладки: без своих фото — как в примере, иначе число фото в пределах раскладки */

const gapOf = id => {
  const g = GAPS.find(x => x[0] === id) || GAPS[2];
  return { g: g[2], r: g[3] };
};
/** Единицы холста → cqw (ширина холста 1000 единиц = 100cqw) */
const cq = n => (n / 10).toFixed(2) + 'cqw';
const pos = (x, y, w, h) => 'left:' + cq(x) + ';top:' + cq(y) + ';width:' + cq(w) + ';height:' + cq(h);
/** Сетка c × r в прямоугольнике a с зазором g, по строкам */
function gridIn(a, c, r, g) {
  const w = (a.w - g * (c - 1)) / c,
    h = (a.h - g * (r - 1)) / r,
    out = [];
  for (let j = 0; j < r; j++) for (let i = 0; i < c; i++) out.push({ x: a.x + i * (w + g), y: a.y + j * (h + g), w, h });
  return out;
}
/** Ряд ячеек по весам: по ширине или (vert) по высоте */
function rowIn(a, ws, g, vert) {
  const t = ws.reduce((s, v) => s + v, 0),
    len = (vert ? a.h : a.w) - g * (ws.length - 1);
  let p = vert ? a.y : a.x;
  return ws.map(v => {
    const l = (len * v) / t,
      c = vert ? { x: a.x, y: p, w: a.w, h: l } : { x: p, y: a.y, w: l, h: a.h };
    p += l + g;
    return c;
  });
}
/** Две части: первая — доля p, по высоте (vert) или по ширине */
function split(a, p, g, vert) {
  if (vert) {
    const h = (a.h - g) * p;
    return [
      { x: a.x, y: a.y, w: a.w, h },
      { x: a.x, y: a.y + h + g, w: a.w, h: a.h - h - g },
    ];
  }
  const w = (a.w - g) * p;
  return [
    { x: a.x, y: a.y, w, h: a.h },
    { x: a.x + w + g, y: a.y, w: a.w - w - g, h: a.h },
  ];
}
/** Карточки «вразброс» (полароиды, мудборд): сетка с самыми крупными карточками (перебор числа колонок), последний ряд по центру, сдвиг jit и наклон rot от ГПСЧ;
    o.k — пропорции карточки (высота к ширине) для подбора сетки, asp(i, R) — у каждой своя */
function scatter(k, a, R, o) {
  let cols = 1,
    best = 0;
  for (let c = 1; c <= k; c++) {
    const r = Math.ceil(k / c),
      s = Math.min(a.w / c, a.h / r / o.k);
    if (s > best + 0.5) {
      best = s;
      cols = c;
    }
  }
  const rows = Math.ceil(k / cols),
    cw = a.w / cols,
    ch = a.h / rows,
    out = [];
  for (let i = 0; i < k; i++) {
    const row = Math.floor(i / cols),
      inRow = row === rows - 1 ? k - row * cols : cols,
      col = i % cols,
      ox = ((cols - inRow) * cw) / 2,
      asp = o.asp(i, R),
      sc = o.sc ? o.sc(R) : 1;
    const w = Math.min(cw * o.fill, (ch * o.fill) / asp) * sc,
      h = w * asp,
      cx = a.x + ox + col * cw + cw / 2 + (R() - 0.5) * cw * o.jit,
      cy = a.y + row * ch + ch / 2 + (R() - 0.5) * ch * o.jit;
    out.push({ x: cx - w / 2, y: cy - h / 2, w, h, rot: (R() - 0.5) * o.rot, z: 1 + Math.floor(R() * k) });
  }
  return out;
}
/** Подпись на холсте: класс, роль (data-cl-tx: cap — подпись, mag — заголовок журнала), место и кегль в единицах; текст — во внутреннем <b> (многоточие у длинной строки) */
const word = (cls, role, st, fs, t) =>
  `<span class="cl-w ${cls}"${role ? ` data-cl-tx="${role}"` : ''} style="${st};font-size:${cq(fs)}"><b>${esc(t)}</b></span>`;
/** Раскладки: (k, a — место под фото, g — зазор, R — ГПСЧ, x — {W, H, text, rad}) → ячейки [{x, y, w, h, r — скругление, rot, z, cls, circ, p — поля карточки, cap}]
    или {cells, pre — подложка под ячейками, deco — поверх} */
const GEN = {
  g22: (k, a, g) => gridIn(a, 2, 2, g),
  g23: (k, a, g) => (a.h >= a.w ? gridIn(a, 2, 3, g) : gridIn(a, 3, 2, g)),
  g33: (k, a, g) => gridIn(a, 3, 3, g),
  hero2: (k, a, g) => {
    const wide = a.w >= a.h * 0.95,
      [b, rest] = split(a, 0.62, g, !wide);
    return [b, ...rowIn(rest, [1, 1], g, wide)];
  },
  hero: (k, a, g) => {
    const tall = a.h >= a.w * 0.8,
      [b, rest] = split(a, 0.64, g, tall);
    return [b, ...rowIn(rest, Array(k - 1).fill(1), g, !tall)];
  },
  /* кладка: колонки равной ширины, фото по кругу слева направо, высоты ячеек — от ГПСЧ (одно и то же при каждой отрисовке) */
  mosaic: (k, a, g, R) => {
    const c = a.w > a.h * 1.25 ? (k <= 5 ? 3 : 4) : k <= 4 ? 2 : 3,
      out = [];
    rowIn(a, Array(c).fill(1), g, false).forEach((col, j) => {
      const idx = [];
      for (let i = j; i < k; i += c) idx.push(i);
      rowIn(
        col,
        idx.map(() => 0.7 + R() * 0.7),
        g,
        true,
      ).forEach((cell, m) => {
        out[idx[m]] = cell;
      });
    });
    return out;
  },
  /* плёнка: тёмная лента с перфорацией, кадры 3:2, дата оранжевым в углу последнего кадра (как у плёночной мыльницы), подпись под лентой.
    Высокий холст — одна лента стоит; иначе от четырёх кадров — две ленты лёжа одна под другой (квадрат заполнен, а не узкая полоса), три кадра — одна лента */
  film: (k, a, g, R, x) => {
    const fr = 1.5,
      cap = x.text ? Math.max(90, Math.min(150, a.h * 0.12)) : 0,
      AH = a.h - cap,
      tall = a.h >= a.w * 1.15,
      two = !tall && k >= 4,
      vert = tall || (!two && a.w <= a.h * 1.3),
      cells = [];
    const strip = (sx, sy, SW, SH, m) => `<span class="cl-d fs" style="${pos(sx, sy, SW, SH)};border-radius:${cq(m * 0.05)}"></span>`;
    let pre = '',
      deco = '',
      capY;
    if (vert) {
      const q = k + 0.07 * (k - 1) + 0.3,
        fw = Math.min((a.w * 0.9) / 1.36, (AH * fr) / q),
        fh = fw / fr,
        SW = fw * 1.36,
        SH = fh * q,
        sx = a.x + (a.w - SW) / 2,
        sy = a.y + (a.h - SH - cap) / 2;
      for (let i = 0; i < k; i++) cells.push({ x: sx + fw * 0.18, y: sy + fh * 0.15 + i * fh * 1.07, w: fw, h: fh, r: fw * 0.02 });
      const pf = px =>
        `<span class="cl-d pf" style="${pos(px, sy + fh * 0.06, fw * 0.075, SH - fh * 0.12)};--pp:${cq(fh * 0.2)};--ph:${cq(fh * 0.1)}"></span>`;
      pre = strip(sx, sy, SW, SH, fh);
      deco = pf(sx + fw * 0.05) + pf(sx + SW - fw * 0.125);
      capY = sy + SH;
    } else {
      const per = two ? [Math.ceil(k / 2), k - Math.ceil(k / 2)] : [k],
        m = per[0];
      let fw = (a.w * 0.94) / (m + 0.05 * (m - 1) + 0.2),
        fh = fw / fr,
        SH = fh * 1.36,
        gs = fh * 0.24,
        tot = per.length * SH + (per.length - 1) * gs;
      if (tot > AH * 0.96) {
        const s = (AH * 0.96) / tot;
        fw *= s;
        fh *= s;
        SH *= s;
        gs *= s;
        tot *= s;
      }
      let sy = a.y + (a.h - tot - cap) / 2;
      per.forEach(cnt => {
        const SW = fw * (cnt + 0.05 * (cnt - 1) + 0.2),
          sx = a.x + (a.w - SW) / 2;
        pre += strip(sx, sy, SW, SH, fh);
        for (let j = 0; j < cnt; j++) cells.push({ x: sx + fw * 0.1 + j * fw * 1.05, y: sy + fh * 0.18, w: fw, h: fh, r: fw * 0.02 });
        const pf = py =>
          `<span class="cl-d pf h" style="${pos(sx + fh * 0.06, py, SW - fh * 0.12, fh * 0.075)};--pp:${cq(fh * 0.2)};--ph:${cq(fh * 0.1)}"></span>`;
        deco += pf(sy + fh * 0.045) + pf(sy + SH - fh * 0.12);
        sy += SH + gs;
      });
      capY = sy - gs;
    }
    const c = cells[k - 1];
    deco += word(
      'fe',
      '',
      `left:${cq(c.x + c.w * 0.5)};top:${cq(c.y + c.h * 0.8)};width:${cq(c.w * 0.44)};height:${cq(c.h * 0.13)}`,
      c.h * 0.085,
      stamp(),
    );
    if (cap) deco += word('fcap', 'cap', pos(a.x, capY, a.w, cap), cap * 0.38, x.text);
    return { cells, pre, deco };
  },
  /* полароиды: белые карточки с полем снизу, наклон ±4,5°, подпись на первой */
  pol: (k, a, g, R, x) => {
    const cells = scatter(k, a, R, { k: 1.2, fill: 0.9, jit: 0.08, rot: 9, asp: () => 1.2 });
    cells.forEach((c, i) => {
      const s = c.w;
      c.cls = 'pol';
      c.r = s * 0.012;
      c.p = [s * 0.06, s * 0.06, s * 0.26, s * 0.06];
      c.pr = s * 0.006;
      if (i === 0 && x.text) c.cap = word('pcap', 'cap', `left:0;right:0;bottom:0;height:${cq(s * 0.26)}`, s * 0.105, x.text);
    });
    return cells;
  },
  /* журнал: дата и заголовок сверху, черта, крупное фото и сноски с номерами */
  mag: (k, a, g, R, x) => {
    const hh = Math.min(a.h * 0.22, 240),
      b = { x: a.x, y: a.y + hh, w: a.w, h: a.h - hh },
      wide = b.w > b.h * 1.3,
      [big, rest] = split(b, wide ? 0.55 : 0.6, g, false),
      n = k - 1;
    const small = wide && n > 2 ? gridIn(rest, 2, Math.ceil(n / 2), g).slice(0, n) : rowIn(rest, Array(n).fill(1), g, true),
      cells = [big, ...small];
    let deco =
      word('mk', '', `left:${cq(a.x)};top:${cq(a.y)};width:${cq(a.w)};height:${cq(hh * 0.24)}`, hh * 0.13, today().toUpperCase()) +
      word(
        'mt',
        'mag',
        `left:${cq(a.x)};top:${cq(a.y + hh * 0.24)};width:${cq(a.w)};height:${cq(hh * 0.52)}`,
        hh * 0.42,
        x.text || magTitle(),
      ) +
      `<span class="cl-d rule" style="${pos(a.x, a.y + hh * 0.84, a.w, Math.max(2.5, hh * 0.012))}"></span>`;
    small.forEach((c, i) => {
      deco += word(
        'num',
        '',
        `left:${cq(c.x + c.w * 0.06)};top:${cq(c.y + c.w * 0.05)};height:${cq(Math.min(c.w, c.h) * 0.16)}`,
        Math.min(c.w, c.h) * 0.11,
        '0' + (i + 2),
      );
    });
    return { cells, deco };
  },
  /* сторис: ленты во всю ширину (на широком холсте — столбцы), средние делятся пополам */
  story: (k, a, g) => {
    const P = { 2: [1, 1], 3: [1, 1, 1], 4: [1, 2, 1], 5: [2, 1, 2], 6: [2, 2, 2] }[k] || [1, 1],
      wide = a.w > a.h * 1.1,
      out = [];
    rowIn(
      a,
      P.map(() => 1),
      g,
      !wide,
    ).forEach((b, j) => out.push(...rowIn(b, Array(P[j]).fill(1), g, wide)));
    return out;
  },
  /* до и после: две половины (на высоком холсте — одна под другой), метки; без отступов — черта с ручкой на стыке */
  ba: (k, a, g) => {
    const tall = a.h > a.w * 1.1,
      cells = rowIn(a, [1, 1], g, tall),
      m = Math.min(a.w, a.h);
    let deco = ['До', 'После']
      .map((t, i) => {
        const c = cells[i];
        return word(
          'lbl',
          '',
          `left:${cq(c.x + m * 0.04)};top:${cq(c.y + m * 0.04)};height:${cq(m * 0.085)};padding:0 ${cq(m * 0.032)};border-radius:${cq(m * 0.0425)}`,
          m * 0.042,
          t.toUpperCase(),
        );
      })
      .join('');
    if (!g) {
      const c = cells[1],
        ln = tall ? pos(a.x, c.y - 2, a.w, 4) : pos(c.x - 2, a.y, 4, a.h),
        d = m * 0.09,
        hx = tall ? a.x + a.w / 2 : c.x,
        hy = tall ? c.y : a.y + a.h / 2;
      deco += `<span class="cl-d dv" style="${ln}"></span><span class="cl-d hd" style="${pos(hx - d / 2, hy - d / 2, d, d)}"></span>`;
    }
    return { cells, deco };
  },
  /* круги: крупный круг в центре и малые вокруг (кольцо цвета фона отделяет их); два фото — два круга внахлёст */
  circles: (k, a, g) => {
    const m = Math.min(a.w, a.h),
      cx = a.x + a.w / 2,
      cy = a.y + a.h / 2,
      ring = { circ: true, cls: 'circ' };
    if (k === 2) {
      const wide = a.w >= a.h,
        d = wide ? Math.min(a.w * 0.56, a.h * 0.9) : Math.min(a.h * 0.56, a.w * 0.9),
        o = d * 0.42;
      return [-1, 1].map((s, i) =>
        Object.assign({ x: cx + (wide ? s * o : 0) - d / 2, y: cy + (wide ? 0 : s * o) - d / 2, w: d, h: d, z: i + 1 }, ring),
      );
    }
    const D = m * 0.5,
      d = m * (k <= 4 ? 0.34 : 0.28),
      rx = ((a.w - d) / 2) * 0.94,
      ry = ((a.h - d) / 2) * 0.94,
      out = [Object.assign({ x: cx - D / 2, y: cy - D / 2, w: D, h: D, z: 3 }, ring)];
    for (let i = 0; i < k - 1; i++) {
      const t = ((-135 + (360 * i) / (k - 1)) * Math.PI) / 180;
      out.push(Object.assign({ x: cx + Math.cos(t) * rx - d / 2, y: cy + Math.sin(t) * ry - d / 2, w: d, h: d, z: 1 }, ring));
    }
    return out;
  },
  /* мудборд: снимки с белой каймой вразброс, наклон ±8°, полоски скотча, заголовок наклейкой */
  mood: (k, a, g, R, x) => {
    const cells = scatter(k, a, R, {
      k: 1.05,
      fill: 0.96,
      jit: 0.16,
      rot: 16,
      asp: (i, r) => [1.25, 0.8, 1][Math.floor(r() * 3)],
      sc: r => 0.86 + r() * 0.26,
    });
    let deco = '';
    cells.forEach((c, i) => {
      const s = Math.min(c.w, c.h);
      c.cls = 'mood';
      c.r = s * 0.01;
      c.p = [s * 0.035, s * 0.035, s * 0.035, s * 0.035];
      c.pr = s * 0.004;
      if (i % 2 === 0) {
        const tw = s * 0.34,
          th = s * 0.09;
        deco += `<span class="cl-d tape" style="${pos(c.x + c.w / 2 - tw / 2, c.y - th * 0.45, tw, th)};transform:rotate(${(c.rot + (R() - 0.5) * 18).toFixed(1)}deg);z-index:${k + 2}"></span>`;
      }
    });
    if (x.text) {
      const m = Math.min(a.w, a.h),
        fs = m * 0.06;
      deco += word(
        'stk',
        'cap',
        `left:${cq(a.x + a.w * 0.05)};top:${cq(a.y + a.h * 0.82)};height:${cq(fs * 2)};padding:0 ${cq(fs * 0.7)};border-radius:${cq(fs * 0.3)};z-index:${k + 3}`,
        fs,
        x.text,
      );
    }
    return { cells, deco };
  },
  /* панорама: столбцы во всю высоту через стыки слайдов (стык попадает внутрь фото — карусель листается без шва); линии разреза и номера слайдов */
  pano: (k, a, g, R, x) => {
    const s = slidesOf(k),
      P = {
        2: [
          [1, 1],
          [0.44, 0.56],
        ],
        3: [
          [1, 1, 1],
          [0.3, 0.4, 0.3],
        ],
        4: [
          [1, 2, 1],
          [0.3, 0.4, 0.3],
        ],
        5: [
          [1, 1, 2, 1],
          [0.24, 0.3, 0.22, 0.24],
        ],
        6: [
          [1, 2, 1, 2],
          [0.2, 0.32, 0.24, 0.24],
        ],
      }[k] || [
        [1, 1],
        [0.5, 0.5],
      ],
      out = [];
    rowIn(a, P[1], g, false).forEach((col, j) => out.push(...rowIn(col, Array(P[0][j]).fill(1), g, true)));
    let deco = '';
    const sw = U / s,
      m = Math.min(sw, x.H);
    for (let j = 0; j < s; j++) {
      if (j) deco += `<span class="cl-d cut" style="${pos(j * sw - 3, 0, 6, x.H)};--dl:${cq(m * 0.05)};--dg:${cq(m * 0.085)}"></span>`;
      deco += word(
        'sn',
        '',
        `left:${cq(j * sw + sw / 2 - m * 0.15)};top:${cq(x.H - m * 0.17)};width:${cq(m * 0.3)};height:${cq(m * 0.11)};border-radius:${cq(m * 0.055)}`,
        m * 0.065,
        j + 1 + ' / ' + s,
      );
    }
    return { cells: out, deco };
  },
};
/** Шестнадцатеричный цвет → [r, g, b] */
const rgbOf = h => {
  const m = /^#?([\da-f]{6})$/i.exec(String(h || ''));
  const v = m ? parseInt(m[1], 16) : 0x999999;
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};
const hexOf = a =>
  '#' +
  a
    .map(v =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
/** Смесь двух цветов: t — доля второго */
const mix = (a, b, t) => {
  const x = rgbOf(a),
    y = rgbOf(b);
  return hexOf(x.map((v, i) => v + (y[i] - v) * t));
};
const lum = h => {
  const [r, g, b] = rgbOf(h);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};
/** Переменные фона холста: --clb — фон (и цвет дырок перфорации, колец «Кругов»), --cli — цвет текста. «Цвет из фото» — средний цвет первого фото, осветлённый;
    «Размытие» — сам снимок под размытием (картинка .cl-bl), --clb — его средний цвет */
function bgVars(bg, f0) {
  let b = '#ffffff',
    i = '#151515';
  const t = (f0 && f0.tone) || '#b9b2aa';
  if (bg === 'black') {
    b = '#0f0f10';
    i = '#f3f3f3';
  } else if (bg === 'paper') {
    b = '#efe9de';
    i = '#2b2622';
  } else if (bg === 'tone') {
    b = mix(t, '#ffffff', 0.42);
    i = lum(b) > 0.56 ? '#1a1a1a' : '#fafafa';
  } else if (bg === 'blur') {
    b = t;
    i = '#ffffff';
  }
  return '--clb:' + b + ';--cli:' + i;
}
/** Сегодня: «7 октября 2026»; дата на плёнке — «07.10.26»; заголовок журнала без текста — «Октябрь 2026» */
const today = () => {
  const d = new Date();
  return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
};
const stamp = () => {
  const d = new Date(),
    p = v => String(v).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + String(d.getFullYear()).slice(2);
};
const magTitle = () => {
  const d = new Date();
  return MONN[d.getMonth()] + ' ' + d.getFullYear();
};
/** Ячейка: фото (обёртка .cl-p — поля карточки у полароида и мудборда), пустая (в форме — загрузка) */
function cellHTML(c, i, f, o, rad) {
  const r = c.r != null ? c.r : rad,
    cls = c.cls ? ' ' + c.cls : '',
    st = `${pos(c.x, c.y, c.w, c.h)};border-radius:${c.circ ? '50%' : cq(r)}${c.rot ? `;transform:rotate(${c.rot.toFixed(2)}deg)` : ''}${c.z ? `;z-index:${c.z}` : ''}`;
  if (!f)
    return o.live
      ? `<label class="cl-c em${cls}" style="${st}" data-cl-slot="${i}" aria-label="Добавить фото"><input type="file" accept="image/*" multiple data-cl-file hidden><i>${ic('plus')}</i></label>`
      : `<span class="cl-c em${cls}" style="${st}"></span>`;
  const pd = c.p
    ? ` style="top:${cq(c.p[0])};right:${cq(c.p[1])};bottom:${cq(c.p[2])};left:${cq(c.p[3])};border-radius:${cq(c.pr || 0)}"`
    : '';
  return `<span class="cl-c ph${cls}"${o.live ? ` data-cl-slot="${i}"` : ''} style="${st}"><span class="cl-p"${pd}><img src="${esc(f.url)}" alt="" draggable="false" style="object-position:50% ${f.pos}%${f.f ? ';filter:' + esc(f.f) : ''}"></span>${c.cap || ''}</span>`;
}
/** Коллаж разметкой — наш шаблонизатор. o: L — раскладка, k — ячеек, files — фото по ячейкам (нет — пустая ячейка), ar, gap, bg, text;
    live — центральная карточка формы (ячейки с фото — цели перетаскивания, пустые — загрузка). → .cl-cv: заполняет рамку (position absolute, inset 0) в пропорциях canvasAr */
function collageHTML(o) {
  const L = o.L,
    k = o.k,
    [aw, ah] = canvasAr(L, k, o.ar),
    W = U,
    H = (U * ah) / aw,
    G = gapOf(o.gap),
    g = L.id === 'pano' ? G.g * 0.6 : G.g,
    text = String(o.text || '').trim(),
    F = o.files || [];
  const band = L.tx === 'band' && !!text,
    bh = band ? Math.min(150, Math.max(96, H * 0.13)) : 0,
    a = { x: g, y: g, w: W - 2 * g, h: H - 2 * g - bh };
  const res = GEN[L.id](k, a, g, rng(k * 31 + L.pop), { W, H, text, rad: G.r }),
    cells = Array.isArray(res) ? res : res.cells,
    f0 = F.find(Boolean);
  let h = res.pre || '';
  cells.forEach((c, i) => {
    h += cellHTML(c, i, F[i], o, L.id === 'pano' ? G.r * 0.6 : G.r);
  });
  if (band) h += word('band', 'cap', pos(a.x, a.y + a.h, a.w, bh), bh * 0.36, text);
  return `<span class="cl-cv${o.bg === 'paper' ? ' paper' : ''}" style="${bgVars(o.bg, f0)};--clr:${(aw / ah).toFixed(4)}">${o.bg === 'blur' && f0 ? `<span class="cl-bl" style="background-image:url('${esc(String(f0.url).replace(/'/g, '%27'))}')"></span>` : ''}${h}${res.deco || ''}</span>`;
}

const IDS = {
  mosaic: 'mosaic',
  g22: 'grid_2x2',
  pol: 'polaroids',
  film: 'film',
  hero2: 'hero_two',
  g33: 'grid_3x3',
  hero: 'hero_strip',
  mag: 'magazine',
  story: 'story',
  circles: 'circles',
  g23: 'grid_2x3',
  ba: 'before_after',
  mood: 'moodboard',
  pano: 'panorama',
};
export const collageLayouts = LAYS.map(layout => ({ ...layout, value: IDS[layout.id] }));
export function collageFrame(options) {
  const L = collageLayouts.find(layout => layout.value === options.layout) || collageLayouts[0];
  const k = options.files.length ? Math.max(L.min, Math.min(L.max, options.files.length)) : L.ex;
  const [w, h] = canvasAr(L, k, options.ratio);
  return {
    ratio: w / h,
    html: collageHTML({
      L,
      k,
      files: options.files,
      ar: options.ratio,
      gap: { none: '0', thin: 's', medium: 'm', wide: 'l' }[options.gap] || 'm',
      bg: options.background === 'photo' ? 'tone' : options.background,
      text: options.caption,
      live: true,
    }),
  };
}
